/**
 * AccessExpiryBanner — Dashboard banner shown whenever the user is on a
 * time-limited grant (redeemed access code, trial, etc.). Displays a live
 * countdown and a discounted "Lock in the year" CTA that redirects to
 * Stripe with an admin-controlled promo code applied.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { AlertTriangle, Sparkles, X, Clock, ArrowRight } from 'lucide-react';

// Renewal promo code baked into the banner CTA — 20% off for graduates of
// the free-trial funnel. Admins can rotate the value via influencer codes.
const RENEWAL_CODE = 'FREETRIAL20';
const RENEWAL_PCT = 20;

const AccessExpiryBanner = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [tick, setTick] = useState(0);
  const [redirecting, setRedirecting] = useState(false);

  // Refresh countdown every minute so "X days, Y hours left" stays live.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    axios.get(`${API}/subscription-tiers`).then((r) => setTiers(r.data || [])).catch(() => {});
  }, [API]);

  const info = useMemo(() => {
    void tick; // re-eval every tick
    const exp = user?.access_expires_at;
    if (!exp) return null;
    const expDt = new Date(exp);
    const now = new Date();
    const ms = expDt.getTime() - now.getTime();
    const days = Math.max(0, Math.floor(ms / 86_400_000));
    const hours = Math.max(0, Math.floor((ms % 86_400_000) / 3_600_000));
    return {
      expDt, ms, days, hours,
      isExpired: ms <= 0,
      isExpiringSoon: ms > 0 && ms <= 7 * 86_400_000,
      role: (user?.role || '').toLowerCase(),
    };
  }, [user?.access_expires_at, user?.role, tick]);

  if (!info || dismissed) return null;

  const proTier = tiers.find((t) => (t.name || '').toLowerCase() === info.role) || tiers[0];
  const annualBase = Number(proTier?.annual_price || 0);
  const annualDiscounted = annualBase * (1 - RENEWAL_PCT / 100);

  const startCheckout = async () => {
    if (!proTier) { navigate('/pricing'); return; }
    setRedirecting(true);
    try {
      const tk = window.localStorage.getItem('token');
      const headers = tk ? { Authorization: `Bearer ${tk}` } : {};
      // Save the promo so the Pricing/checkout flow picks it up if the
      // Stripe redirect fails and the user is dropped back at /pricing.
      try { localStorage.setItem('fda_influencer_code', RENEWAL_CODE); } catch { /* ignore */ }
      const { data } = await axios.post(
        `${API}/checkout/session`,
        {
          items: [{ kind: 'subscription', ref_id: proTier.id, quantity: 1, meta: { billing_cycle: 'annual' } }],
          origin_url: window.location.origin,
          influencer_code: RENEWAL_CODE,
          metadata: { source: 'trial_renewal_banner' },
        },
        { headers },
      );
      if (data?.url) {
        toast.success(`Locking in ${RENEWAL_PCT}% off for the year`);
        window.location.href = data.url;
      } else {
        navigate('/pricing?from=trial');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout — try /pricing');
      navigate('/pricing?from=trial');
    } finally {
      setRedirecting(false);
    }
  };

  const expired = info.isExpired;
  const urgent = expired || info.isExpiringSoon;
  const roleLabel = (info.role || 'trial').toUpperCase();

  return (
    <div
      className={`rounded-2xl border-2 p-4 md:p-5 relative overflow-hidden ${
        expired
          ? 'border-red-500/60 bg-red-50'
          : urgent
            ? 'border-amber-500/60 bg-amber-50'
            : 'border-primary/40 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent'
      }`}
      data-testid="access-expiry-banner"
    >
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 rounded-full p-1 hover:bg-black/5"
        aria-label="Dismiss"
        data-testid="expiry-banner-dismiss"
      >
        <X className="w-4 h-4 opacity-60" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          expired ? 'bg-red-500 text-white' : urgent ? 'bg-amber-500 text-white' : 'bg-primary text-primary-foreground'
        }`}>
          {expired ? <X className="w-5 h-5" /> : urgent ? <AlertTriangle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
            {expired ? `${roleLabel} trial ended` : `${roleLabel} trial — ${info.days}d ${info.hours}h left`}
          </div>
          <div className="font-heading text-base md:text-lg font-black mt-0.5 leading-tight">
            {expired
              ? `Your free ${roleLabel.toLowerCase()} access ended. Lock in the year with ${RENEWAL_PCT}% off before your data goes read-only.`
              : `Loving ${roleLabel.toLowerCase()}? Lock it in for the year with ${RENEWAL_PCT}% off — code ${RENEWAL_CODE}.`}
          </div>
          {proTier && annualBase > 0 && (
            <div className="text-xs text-muted-foreground mt-1">
              Regular annual <span className="line-through">${annualBase.toFixed(2)}</span>
              <span className="ml-2 font-bold text-emerald-700">
                ${annualDiscounted.toFixed(2)} with {RENEWAL_CODE}
              </span>
              <span className="ml-1">— save ${(annualBase - annualDiscounted).toFixed(2)}.</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-3">
            <Button
              onClick={startCheckout}
              size="sm"
              disabled={redirecting}
              className="rounded-full font-bold"
              data-testid="expiry-banner-renew-btn"
            >
              {redirecting ? 'Redirecting…' : `Lock in annual · ${RENEWAL_PCT}% off`}
              {!redirecting && <ArrowRight className="ml-1.5 w-3 h-3" />}
            </Button>
            <Button
              onClick={() => navigate('/pricing')}
              size="sm"
              variant="outline"
              className="rounded-full font-bold"
              data-testid="expiry-banner-plans-btn"
            >
              <Sparkles className="mr-1.5 w-3 h-3" /> Compare plans
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessExpiryBanner;
